import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, KeyRound, Plus, Trash2, RefreshCw, Copy, Check, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { apiKeysApi, formatApiError } from "../../lib/ledgerApi";

const ALL_SCOPES = ["read", "write", "webhooks", "admin"];

const SCOPE_DESCRIPTIONS = {
  read: "GET-only access to wallets, transactions, invoices, contracts.",
  write: "POST/PATCH/DELETE for resources owned by this business.",
  webhooks: "Manage webhook subscriptions + deliveries.",
  admin: "Superset — implies all other scopes. Use sparingly.",
};

function ScopePill({ scope }) {
  const tone = scope === "admin"
    ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300"
    : scope === "write"
      ? "border-foreground/30 bg-foreground/5"
      : "border-border bg-muted/30 text-muted-foreground";
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest font-mono ${tone}`}>
      {scope}
    </span>
  );
}

function CopyButton({ value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => {
        navigator.clipboard.writeText(value);
        setDone(true);
        toast.success("Copied");
        setTimeout(() => setDone(false), 1500);
      }}
      className="p-1.5 border border-border rounded-sm hover:border-foreground"
      title="Copy"
    >
      {done ? <Check className="w-3 h-3" strokeWidth={1.5} /> : <Copy className="w-3 h-3" strokeWidth={1.5} />}
    </button>
  );
}

function MintDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["read"]);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(null); // {secret, key_prefix, name}
  const [showSecret, setShowSecret] = useState(true);

  const toggleScope = (s) => {
    setScopes((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const onMint = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (scopes.length === 0) {
      toast.error("Pick at least one scope");
      return;
    }
    setBusy(true);
    try {
      const r = await apiKeysApi.mint({ name: name.trim(), scopes });
      setRevealed(r);
      onCreated?.(r);
    } catch (e) {
      toast.error("Mint failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="apikey-mint-dialog">
      <div className="w-full max-w-lg mx-4 border border-border bg-card rounded-sm p-6">
        {!revealed ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium tracking-tight inline-flex items-center gap-2">
                <KeyRound className="w-4 h-4" strokeWidth={1.5} /> Mint API key
              </h2>
              <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
            </div>

            <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Name</label>
            <input
              data-testid="apikey-mint-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. zapier-prod"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono mb-5"
            />

            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Scopes</div>
            <div className="space-y-2 mb-6">
              {ALL_SCOPES.map((s) => (
                <label key={s} data-testid={`apikey-scope-${s}`} className="flex items-start gap-3 cursor-pointer p-2 border border-border rounded-sm hover:border-foreground/40">
                  <input
                    type="checkbox"
                    checked={scopes.includes(s)}
                    onChange={() => toggleScope(s)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <ScopePill scope={s} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{SCOPE_DESCRIPTIONS[s]}</p>
                  </div>
                </label>
              ))}
            </div>

            <button
              onClick={onMint}
              disabled={busy || !name.trim() || scopes.length === 0}
              data-testid="apikey-mint-submit"
              className="w-full text-xs border border-foreground bg-foreground text-background px-4 py-2.5 rounded-sm uppercase tracking-widest disabled:opacity-50"
            >
              {busy ? "Minting…" : "Mint key"}
            </button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
              <h2 className="text-lg font-medium tracking-tight">Save this secret now</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              We will <strong>never show it again</strong>. Paste it into your Zapier app, SDK config, or env file before closing this dialog.
            </p>

            <label className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Secret</label>
            <div className="flex items-center gap-2 mb-2">
              <input
                data-testid="apikey-secret-reveal"
                readOnly
                type={showSecret ? "text" : "password"}
                value={revealed.secret}
                className="flex-1 bg-background border border-amber-500/40 rounded-sm px-3 py-2 text-xs font-mono"
              />
              <button onClick={() => setShowSecret((v) => !v)} className="p-2 border border-border rounded-sm hover:border-foreground" title="Toggle visibility">
                {showSecret ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </button>
              <CopyButton value={revealed.secret} testid="apikey-secret-copy" />
            </div>

            <div className="text-xs text-muted-foreground mb-1">Use it like:</div>
            <pre className="text-[11px] font-mono bg-background border border-border rounded-sm p-3 mb-5 overflow-x-auto">{`Authorization: Bearer ${revealed.secret}`}</pre>

            <button
              onClick={onClose}
              data-testid="apikey-secret-done"
              className="w-full text-xs border border-foreground bg-foreground text-background px-4 py-2.5 rounded-sm uppercase tracking-widest"
            >
              I&apos;ve copied it — close
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function RotateDialog({ keyDoc, onClose, onRotated }) {
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);

  const onConfirm = async () => {
    setBusy(true);
    try {
      const r = await apiKeysApi.rotate(keyDoc.id);
      setRevealed(r);
      onRotated?.(r);
    } catch (e) {
      toast.error("Rotate failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="apikey-rotate-dialog">
      <div className="w-full max-w-lg mx-4 border border-border bg-card rounded-sm p-6">
        {!revealed ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
              <h2 className="text-lg font-medium tracking-tight">Rotate API key</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Rotating mints a NEW secret for <span className="font-mono">{keyDoc.name}</span> ({keyDoc.key_prefix}…).
            </p>
            <p className="text-xs text-muted-foreground mb-5">
              The old secret keeps working for a 60-minute grace window so you can update integrations without downtime.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground px-3 py-2">Cancel</button>
              <button
                onClick={onConfirm}
                disabled={busy}
                data-testid="apikey-rotate-confirm"
                className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest"
              >
                {busy ? "Rotating…" : "Rotate"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
              <h2 className="text-lg font-medium tracking-tight">New secret — save it now</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              Old secret valid until <span className="font-mono">{revealed.rotation_grace_until ? new Date(revealed.rotation_grace_until).toLocaleString() : "—"}</span>.
            </p>
            <input
              data-testid="apikey-rotate-secret"
              readOnly
              value={revealed.secret}
              className="w-full bg-background border border-amber-500/40 rounded-sm px-3 py-2 text-xs font-mono mb-3"
            />
            <div className="flex gap-2 justify-end">
              <CopyButton value={revealed.secret} testid="apikey-rotate-secret-copy" />
              <button
                onClick={onClose}
                data-testid="apikey-rotate-done"
                className="text-xs border border-foreground bg-foreground text-background px-4 py-2 rounded-sm uppercase tracking-widest"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMint, setShowMint] = useState(false);
  const [rotateTarget, setRotateTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiKeysApi.list();
      setKeys(r);
    } catch (e) {
      toast.error("Failed to load keys", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onRevoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any integration using it will break immediately.`)) return;
    try {
      await apiKeysApi.revoke(k.id);
      toast.success("Revoked");
      load();
    } catch (e) {
      toast.error("Revoke failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const active = keys.filter((k) => !k.revoked_at);
  const revoked = keys.filter((k) => k.revoked_at);

  return (
    <div className="max-w-5xl px-6 md:px-10 py-10 md:py-14" data-testid="apikeys-page">
      <Link to="/app/settings" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to settings
      </Link>
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ Settings · API keys</div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium flex items-center gap-3">
            <KeyRound className="w-6 h-6" strokeWidth={1.5} /> Business API keys
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Long-lived <code className="font-mono">neopay_bk_*</code> keys for SDKs, Zapier, and your own server-side integrations.
            Use these instead of JWTs for anything that runs without a human.
          </p>
        </div>
        <button
          onClick={() => setShowMint(true)}
          data-testid="apikey-mint-btn"
          className="text-xs border border-foreground bg-foreground text-background px-4 py-2.5 rounded-sm uppercase tracking-widest inline-flex items-center gap-1.5"
        >
          <Plus className="w-3 h-3" strokeWidth={1.5} /> Mint key
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-sm text-muted-foreground">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-8 text-center" data-testid="apikey-empty">
          <KeyRound className="w-8 h-8 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
          <div className="text-sm">No API keys yet</div>
          <p className="text-xs text-muted-foreground mt-1">Mint your first key to start integrating.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-8" data-testid="apikey-list">
            {active.map((k) => (
              <div key={k.id} data-testid={`apikey-row-${k.id}`} className="border border-border rounded-sm bg-card p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm truncate">{k.name}</div>
                    {k.scopes.map((s) => <ScopePill key={s} scope={s} />)}
                    {k.rotation_grace_until && (
                      <span className="text-[10px] uppercase tracking-widest border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300 px-1.5 py-0.5 rounded-sm">
                        rotating
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-muted-foreground mt-1 truncate">
                    neopay_bk_{k.key_prefix}…{k.previous_key_prefix ? ` (was: ${k.previous_key_prefix}…)` : ""}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Created {new Date(k.created_at).toLocaleDateString()} by {k.created_by_user_email || "—"}
                    {" · "}
                    Last used {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setRotateTarget(k)}
                    data-testid={`apikey-rotate-${k.id}`}
                    className="text-xs border border-border px-3 py-1.5 rounded-sm hover:border-foreground inline-flex items-center gap-1.5"
                  >
                    <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Rotate
                  </button>
                  <button
                    onClick={() => onRevoke(k)}
                    data-testid={`apikey-revoke-${k.id}`}
                    className="text-xs border border-border px-3 py-1.5 rounded-sm hover:border-status-tampered text-muted-foreground hover:text-status-tampered inline-flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>

          {revoked.length > 0 && (
            <details className="border border-border rounded-sm bg-card p-4" data-testid="apikey-revoked-list">
              <summary className="text-xs text-muted-foreground cursor-pointer">
                {revoked.length} revoked {revoked.length === 1 ? "key" : "keys"}
              </summary>
              <div className="mt-3 space-y-2">
                {revoked.map((k) => (
                  <div key={k.id} className="text-xs flex items-center gap-2 opacity-60">
                    <span className="font-mono">neopay_bk_{k.key_prefix}…</span>
                    <span>— {k.name}</span>
                    <span className="text-muted-foreground">— revoked {new Date(k.revoked_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {showMint && <MintDialog onClose={() => { setShowMint(false); load(); }} />}
      {rotateTarget && <RotateDialog keyDoc={rotateTarget} onClose={() => { setRotateTarget(null); load(); }} />}
    </div>
  );
}
